import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import {
	Modal,
	Button,
	ProgressIndicator,
	DataGrid,
	Toolbar
} from "rhombus-react";

import { fetchAllVersions } from "reducers/CreativeVersions";
import { selectMultiple, clearLevel } from "reducers/Selected";

import { noop, TextOverflow } from "utils/common";

import Breadcrumb from "./Breadcrumb";
import SelectItem from "widgets/grid/cells/SelectItem";
import SelectAllCell from "widgets/grid/cells/SelectAllCell";
import SelectedRow from "utils/columns/SelectedRow";
import NumberEditor from "widgets/grid/editors/Number";
import { WEIGHT_MULTIPLIER_DECIMAL_PLACES } from "utils/constants";
import { useTooltipRebuild } from "hooks/useTooltipRebuild";

import "./SelectVersions.css";

const selectionNamespace = "multiVariateVersions";

export function normalizeWeight(weight){
	/*
		`weight` will generally come in as a string, from a "number" input.

		Normalized weights must be numbers, and they're restricted to 2 decimal placees
		because of XRE restrictions.
	*/
	let floatWeight = parseFloat(weight);
	if(Number.isNaN(floatWeight) || (floatWeight < 0)){
		// If we get empty strings, or other invalid input, just default to 1x.
		return 1;
	}

	let fixedDecimalString = floatWeight.toFixed(WEIGHT_MULTIPLIER_DECIMAL_PLACES);
	return parseFloat(fixedDecimalString); // Convert back to a number
}

function multiplierFormatter({ value }){
	return `${value}x`;
}

const WEIGHTING_COLUMN = {
	key: "weightMultiplier",
	name: "Weight",
	width: 140,
	cellClass: "weight centered editable",
	formatter: multiplierFormatter,
	editable: true,
	editor: NumberEditor,

	min: 0,
	required: true,
};

const VERSION_SELECT_COLUMNS = [
	{
		key: "name",
		name: "Version Name",
		width: 540,
		formatter: TextOverflow,
	},
	{
		key: "id",
		name: "Version ID",
	},
];

export default function SelectVersions({
	commonModalProps,
	navigationActions: { popModal },

	libraryId,
	creativeId,

	prompt,
	includeWeights,

	onSelect = noop,
	maxSelectableVersions,
}){
	const dispatch = useDispatch();
	const selectedIds = useSelector(state => state.selected[selectionNamespace] || []);
	const decisioningType = useSelector(state => state.creativeDetails.decisioningType);
	const isTooManyVersionsSelected = selectedIds.length > maxSelectableVersions;

	let numVersionsOver = selectedIds.length - maxSelectableVersions;
	let btnSaveTooltip = `The current selection will<br/>exceed the ${maxSelectableVersions.toLocaleString("en-us")} version<br/>limit by ${numVersionsOver}, please remove <br/>${numVersionsOver === 1 ? 'a version.' : 'some versions.'}`;

	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [versions, setVersions] = useState([]);

	let isAllSelected = useMemo(() => {
		return versions.length
			&& (versions.length === selectedIds.length)
			&& versions.every(version => selectedIds.includes(version.id));
	}, [selectedIds, versions]);

	let numSelected = selectedIds.length;

	function selectIds(ids, checked){
		dispatch(selectMultiple(ids, selectionNamespace, checked));
	}

	function selectAll(checked){
		let allIds = versions.flatMap(item => item.id);
		selectIds(allIds, checked);
	}

	function updateWeights(fromRow, toRow, weightMultiplier, shouldSetSelection = true){
		/*
			shouldSetSelection is an optional flag for when we want a weight change to select the version.

			Generally, if someone updates a weight, we want to add it to the selection, but we
			don't want to change the selection at all if we're just resetting the weights to default.
		*/
		let normalizedWeight = normalizeWeight(weightMultiplier);

		let updated = versions.map((item, index) => {
			if(index >= fromRow && index <= toRow){
				if(shouldSetSelection){
					selectIds([item.id], true);
				}
				return {
					...item,
					weightMultiplier: normalizedWeight,
				};
			}
			return item;
		});

		setVersions(updated);
	}

	function resetWeights(){
		updateWeights(0, versions.length - 1, 1, false);
	}

	useEffect(() => {
		dispatch(clearLevel(selectionNamespace));
		dispatch(fetchAllVersions(libraryId, creativeId)).then((response) => {
			setIsLoading(false);
			if(response?.success){
				let { items = []} = response.payload?.data || {};
				if(decisioningType !== "Weighted Rotation"){
					// If the Versions aren't already manually weighted, initialize weightMultipliers to 1 for this modal.
					items = items.map((item) => {
						return {
							...item,
							weightMultiplier: 1,
						};
					});
				}

				setVersions(items);

				// Initialize selection with Versions that are Live
				let initialSelectedIds = items.reduce((prev, item) => {
					let { id, status } = item || {};
					if(status === "Live"){
						prev.push(id);
					}
					return prev;
				}, []);
				selectIds(initialSelectedIds, true);
			}
		});
	}, []);

	const mappedVersions = useMemo(() => {
		return versions.map((item) => {
			let isSelected = selectedIds.includes(item.id);
			return {
				...item,
				isSelected,
				idSelector: {
					id: item.id,
					isSelected,
					onChange: selectIds,
				},
			};
		});
	}, [versions, selectedIds]);

	function getRow(index){
		return mappedVersions[index];
	}

	let columns = [
		{
			key: "idSelector",
			name: "",
			width: 40,
			cellClass: "select-column",
			formatter: SelectItem,
			headerRenderer: SelectAllCell,
			selectAll,
			isAllSelected,
			getRowMetaData: () => {
				return {
					type: selectionNamespace,
					shiftSelectConfig: {
						selectItems: (state, level) => versions,
					},
				};
			},
		},
		...VERSION_SELECT_COLUMNS,
	];

	if(includeWeights){
		columns.push(WEIGHTING_COLUMN);
	}

	let gridProps = {
		columns,
		rowGetter: getRow,
		rowsCount: versions.length,
		rowRenderer: SelectedRow,
		enableCellSelect: true,
		onGridRowsUpdated({ fromRow, toRow, updated: { weightMultiplier } }){
			updateWeights(fromRow, toRow, weightMultiplier);
		},
	};

	let ResetButton = includeWeights && <Button style="block" onClick={resetWeights}>Set All to 1x</Button>;

	function Content(){
		return (
			<div>
				<div>
					<Breadcrumb currentStep={1} numSelected={numSelected} />
				</div>
				<div className="main-content">
					<ProgressIndicator isLoading={isLoading || isSubmitting} />

					<Toolbar>
						<div className="prompt">{prompt}</div>
						<div>
							{ResetButton}
						</div>
					</Toolbar>

					<div className="grid">
						<DataGrid {...gridProps} />
					</div>
				</div>
			</div>
		);
	}

	useTooltipRebuild(btnSaveTooltip);

	function Footer(){
		function attemptSubmit(){
			let selected = selectedIds.map((id) => {
				let obj = { id };
				let version = versions.find(item => item.id === id);
				if(includeWeights){
					obj.weightMultiplier = version.weightMultiplier;
				}

				return obj;
			});
			setIsSubmitting(true);
			onSelect(selected);
		}

		return (
			<>
				<Button
					theme = "default"
					onClick = {popModal}
					disabled = {isSubmitting}
				>
					Back
				</Button>
				<span
					data-tip={isTooManyVersionsSelected ? btnSaveTooltip : ''}
					data-multiline>
					<Button
						theme = {isTooManyVersionsSelected ? "red" : "green"}
						onClick = {attemptSubmit}
						disabled = {isSubmitting || (selectedIds.length === 0 || isTooManyVersionsSelected)}
					>
						Save and Apply
					</Button>
				</span>
			</>
		);
	}

	let modalProps = {
		...commonModalProps,
		xClose: !isSubmitting,
		className: `${commonModalProps.className} select-versions-modal`,
		renderContent: Content,
		renderFooter: Footer,
	};

	return <Modal {...modalProps} />;
}

SelectVersions.displayName = "SelectVersions";
SelectVersions.propTypes = {
	prompt: PropTypes.string,
	includeWeights: PropTypes.bool,
	maxSelectableVersions: PropTypes.number,
};

SelectVersions.defaultProps = {
	maxSelectableVersions: Infinity,
};
